<template>
  <div class="ProductGallery" :class="{ ProductGallery__Modal: isModal }">
    <!-- Bundle Step Indicator -->
    <ul v-if="isBundleStep" class="ProductGallery__Steps">
      <li v-for="productStep in totalProductSteps" :key="productStep" @click="productStep.step < store.currentStep ? store.step(productStep.step) : {}" class="ProductGallery__Step t5" :class="productStep.step == store.currentStep ? 'ProductGallery__Step--Active' : ''">Step {{ productStep.step }}</li>
    </ul>

    <!-- Quickview PDP Link -->
    <a v-if="location === 'quick-view'" :href="url" class="ProductGallery__QVLink p4" data-color="night-sky-dark"> Get All The Details. <span class="link link--bold">View Full Page</span> </a>

    <!-- Gallery Navigation Thumbnails -->
    <aside class="ProductGallery__Nav" ref="nav">
      <ul class="ProductGallery__Thumbs">
        <li v-for="(asset, index) in assets" :key="asset" @click="move(index)" :class="active === index ? 'active' : ''">
          <figure :class="ready ? 'loaded' : ''">
            <GalleryMedia :media="asset" :zoom="false" :thumbnail="true" :key="asset" @updateModal="updateModal" @displayMedia="displayMedia" />
            <Icon v-if="asset?.sources" :icon="'play'" />
          </figure>
        </li>
      </ul>
    </aside>

    <!-- Gallery Stage -->
    <div class="ProductGallery__Stage" :class="{ 'ProductGallery__Stage--Active': ready }" :ready="ready" :loaded="loaded" :transitioning="transitioning">
      <Splide :options="stageSettings" ref="stage" @splide:mounted="onSliderMount" @splide:refresh="onRefresh" @splide:move="onMove" @splide:moved="onMoved">
        <SplideSlide v-for="(asset, index) in assets" :key="asset">
          <figure :class="ready ? 'loaded' : ''">
            <div v-if="imageBadge && index === 0 && ready" class="ProductGallery__ImageBadge">
              <picture>
                <source media="(min-width:320px)" :srcset="imageBadge" />
                <img loading="lazy" :src="imageBadge" alt="Award Badge" />
              </picture>
            </div>
            <p v-if="textBadge && index === 0 && ready" data-color="night-sky-dark" data-bg-color="white" class="ProductGallery__TextBadge t5">
              {{ textBadge }}
            </p>
            <GalleryMedia :media="asset" :thumbnail="false" @updateModal="updateModal" @displayMedia="displayMedia" :zoom="asset?.sources ? false : true" />
          </figure>
        </SplideSlide>
      </Splide>
      <button role="button" @click="move('-')" class="ProductGallery__ButtonContainer" :class="singleAssetGallery" data-direction="prev" aria-label="prev">
        <div class="btn btn--circle">
          <Icon :icon="'arrow'" />
        </div>
      </button>
      <button role="button" @click="move('+')" class="ProductGallery__ButtonContainer" :class="singleAssetGallery" data-direction="next" aria-label="next">
        <div class="btn btn--circle">
          <Icon :icon="'arrow'" />
        </div>
      </button>
    </div>
    <GalleryZoomPane v-if="zoomedMedia" :media="zoomedMedia" @updateModal="updateModal" />
  </div>
</template>

<script setup>
// Vue Core
import { ref, computed, watch, nextTick } from 'vue';

//Components
import Icon from '../Global/Icon.vue';
import GalleryMedia from './GalleryMedia.vue';
import GalleryZoomPane from './GalleryZoomPane.vue';

// Slider Dependencies
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { stageSettings } from './Composables/Settings';

// State Management
import { useProductStore } from './Composables/Store';

// References
const active = ref(0);
const nav = ref(false);
const stage = ref(false);
const loaded = ref(false);
const transitioning = ref(true);
const zoomedMedia = ref(false);

// Store Declaration
const store = useProductStore();

// Props
const props = defineProps({
  item: { type: Object, required: false, default: false },
  title: { type: String, required: true },
  shortDescription: { type: String, required: false },
  option: { type: String, required: false, default: false },
  gallery: { type: Object, required: false, default: false },
  disableZoom: { type: Boolean, required: false, default: false },
  bundleItem: { type: Object, required: false, default: false },
  isModal: { type: Boolean, required: false, default: false },
  id: { type: String, required: false },
  url: { type: String, required: false },
  location: { type: String, required: false },
  reviewsProduct: { type: String, required: false },
  variant: { type: Object, required: false, default: {} },
});

// Computed
const product = computed(() => {
  return props.location == 'quick-view' ? props.item : store.$state?.item;
});

const imageBadge = computed(() => {
  if (store.$state?.settings?.image_badge_hide) return false;
  const acfImageBadge = product.value?.imageBadge?.value ? JSON.parse(product.value?.imageBadge?.value)[0].src : false;
  const themeImageBadge = store.$state?.settings?.image_badge;
  if (themeImageBadge) {
    return themeImageBadge;
  } else if (acfImageBadge) {
    return acfImageBadge;
  } else {
    return false;
  }
});

const variantBadging = computed(() => {
  const variant = props?.variant || {};
  return variant?.textBadge?.value || false;
});

const textBadge = computed(() => {
  if (store.$state?.settings?.text_badge_hide) return false;
  let acfTextBadge = product.value?.textBadge?.value ? JSON.parse(product.value?.textBadge?.value)[0] : false;
  const themeTextBadge = store.$state?.settings?.text_badge;
  const hasVariantBadge = variantBadging.value;

  if (hasVariantBadge) {
    return variantBadging.value;
  }

  if (themeTextBadge) {
    return themeTextBadge;
  } else if (acfTextBadge) {
    return acfTextBadge;
  } else {
    return false;
  }
});
const isDropship = computed(() => {
  const item = product.value;
  return item && item.tags.find((tag) => tag === 'Marketplace') ? true : false;
});

const isBundleStep = computed(() => props.bundleItem.type == 'product' || props.bundleItem.type == 'review');
const totalProductSteps = computed(() => store?.steps?.filter((step) => step.type === 'product' || step.type === 'review'));

const singleAssetGallery = computed(() => {
  const selectedGallery = assets.value ?? [];
  const isSingle = selectedGallery.length === 1 ?? false;

  return isSingle ? 'ProductGallery__ButtonContainer--Hidden' : '';
});

const ready = computed(() => loaded.value && !transitioning.value);

const onMove = (splide) => {
  const slider = splide.Components.Controller;
  const index = slider.getIndex();
  active.value = index;
  const prevVideo = splide.root.querySelector('.splide__slide.is-active video');
  prevVideo?.pause();
};

const onMoved = async (splide) => {
  await nextTick();

  const video = splide.root.querySelector('.splide__slide.is-active:not(.splide__slide--clone) video');
  const autoplay = video?.getAttribute('data-autoplay') ?? false;
  const controls = splide.root.parentElement.querySelector('.ProductGallery__ButtonContainer').classList.contains('video--has-controls');
  const arrowSpacing = video?.getAttribute('data-arrow-spacing') ?? false;
  const buttons = splide.root.parentElement.querySelectorAll('.ProductGallery__ButtonContainer');

  if (autoplay) {
    video?.play();
  }

  if (controls) {
    buttons.forEach((btn) => {
      btn.classList.remove('video--has-controls');
    });
  } else if (arrowSpacing) {
    buttons.forEach((btn) => {
      btn.classList.add('video--has-controls');
    });
  }
};

const onSliderMount = () => {
  transitioning.value = false;
};

const move = (direction) => {
  const slider = stage.value.splide.Components.Controller;
  return slider.go(direction);
};

const onRefresh = async (splide) => {
  const slider = splide || stage.value.splide;
  slider.Components.Controller.go(0, false);
  await nextTick();

  setTimeout(() => (transitioning.value = false), 300);
};

const transitionSlider = async () => {
  transitioning.value = true;
  await nextTick();
};

const galleryMetaobjectSort = (asset1) => {
  return asset1.alt.includes('METAOBJECT') ? 1 : -1;
};
const getSelectedMedia = () => {
  const gallery = props.gallery;
  let option = props.option;
  const assets = gallery.filter((asset) => {
    if (asset.alt !== '' && !asset.alt.includes('FEED')) {
      let alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
      return alt === option || (!asset.alt.includes('VIDEO') && asset.sources) || asset.infographic;
    }
  });
  const videos = gallery.filter((asset) => asset.sources && asset.alt.includes('VIDEO'));
  const main = assets.filter((asset) => asset.alt.includes('MAIN')).sort(galleryMetaobjectSort);
  const detail = assets.filter((asset) => asset.alt.includes('DETAIL')).sort(galleryMetaobjectSort);
  const lifestyle = assets.filter((asset) => asset.alt.includes('LIFESTYLE')).sort(galleryMetaobjectSort);
  const nonMetaobj = lifestyle.filter((asset) => asset.alt.includes('LIFESTYLE') && !asset.alt.includes('METAOBJECT')).reverse();
  const metaobj = lifestyle.filter((asset) => asset.alt.includes('LIFESTYLE METAOBJECT')).reverse();
  let galleryArray = [...main, ...videos, ...detail, ...nonMetaobj, ...metaobj];

  return galleryArray;
};

const updateModal = (media) => {
  if (!props.disableZoom) {
    zoomedMedia.value = media;
    !media ? ugcVideo('show') : ugcVideo('hide');
  }
};

const displayMedia = (value = true) => {
  if (!value) {
    loaded.value = value;
    return;
  }
  return setTimeout(() => (loaded.value = value), 400);
};
const assets = computed(() => getSelectedMedia());

watch(() => props.option, transitionSlider);
</script>
